// Celery + socket round trip — faithful re-platform of
// `fn.uni_tools.ajaxPostToCelery` + `checkAndWaitForSocketData`
// (universal_fn_tools.js; inventory §4 data row 20 / parity 68–69, 123):
//
//   1. POST (form-encoded, X-CSRFToken + X-Requested-With headers) → {task_id}.
//   2. Listen on the shared socket's `'data'` event for a message whose keys
//      match the filter (loose ==, the legacy `Object.keys(filter).every`).
//   3. Emit-and-poll: re-emit the filter so the socket server replays the
//      result from its cache if it was already delivered; backoff doubles
//      1s → 2s → … while `tm < 20000` (verbatim legacy condition).
//
// Differences from the legacy (documented):
//  - The legacy helper leaks a socket listener per call ("Awful function …
//    REFACTOR this!"); we remove the listener on resolve/cancel.
//  - `cancel()` lets the caller stop the emit loop on unmount/RECALC-restart;
//    the promise then simply never settles (matching the legacy's eternally
//    pending wait when no result arrives).
//
// Home: src/app/ — shared by the project board (Sales/Costing/Pricing tabs)
// and the reports board generation kit (Phase 5b). The original module path,
// pages/project-board/celeryRoundTrip.ts, re-exports from here.

import { getSocket, type SocketDataMessage } from 'app/socketClient';
import { getCookie } from 'helpers/index';

export interface CeleryWait {
  promise: Promise<SocketDataMessage>;
  cancel: () => void;
}

/**
 * A form field value with jQuery `$.param` (non-traditional) semantics:
 * null/undefined → empty string (the key is KEPT), booleans → 'true'/'false',
 * arrays → one `key[]=` entry per element (empty array → key dropped).
 */
export type FormValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

/**
 * Encode a payload exactly like jQuery `$.ajax({data})` does for the legacy
 * report POSTs: `URLSearchParams`-style pairs where arrays get a `[]` suffix
 * (unless the key already ends with `[]`, e.g. the literal `'img[]'` field in
 * fan_data.js:214 / submittal.package.js:442 with `traditional: true` — both
 * spellings serialize to the same `img[]=…` pairs).
 */
export function encodeForm(data: Record<string, FormValue>): string {
  const body = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const name = key.endsWith('[]') ? key : `${key}[]`;
      value.forEach((el) => body.append(name, String(el)));
      return;
    }
    body.append(key, value == null ? '' : String(value));
  });
  return body.toString();
}

/**
 * The bare POST half of the round trip: form-encoded body + the legacy
 * X-CSRFToken (cookie) and X-Requested-With headers, JSON response. The report
 * board's generate dispatches use this WITHOUT a socket wait — the legacy tabs
 * fire `$.ajax` and let the header notifications poll deliver the file (their
 * `Promise.all([fn['create.websocket'], req]).then` blocks are commented out,
 * e.g. unit_technical_data.js:134-153).
 */
export async function postToCelery<T = { task_id: string }>(
  url: string,
  data: Record<string, FormValue>
): Promise<T> {
  const resp = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCookie('csrftoken') ?? '',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: encodeForm(data),
  });
  if (!resp.ok) {
    throw new Error(`postToCelery ${url} failed: ${resp.status}`);
  }
  return (await resp.json()) as T;
}

/** Loose-equality filter match (legacy `msg[e] == filter[e]`). */
function matches(msg: SocketDataMessage, filter: Record<string, unknown>): boolean {
  // eslint-disable-next-line eqeqeq
  return Object.keys(filter).every((k) => (msg as Record<string, unknown>)[k] == filter[k]);
}

/**
 * The socket half of the round trip on its own: wait for the `data` message
 * matching `task_id` (emit/backoff re-ask). Used directly by flows whose POST
 * resolution and socket resolution have separate consumers — e.g. the 5d
 * phx_selection tab, whose legacy view renders the tab shell on the POST
 * success and the report table on the socket message
 * (phx_selection.js:182-211 `$.ajax success` + `Promise.all([socket, req])`).
 */
export function waitForCeleryTask(taskId: string): CeleryWait {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let listener: ((msg: SocketDataMessage) => void) | null = null;
  const socket = getSocket();
  const filter = { task_id: taskId };

  const cleanup = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (listener) {
      socket.off('data', listener);
      listener = null;
    }
  };

  const promise = new Promise<SocketDataMessage>((resolve) => {
    if (cancelled) return;
    listener = (msg: SocketDataMessage) => {
      if (msg && matches(msg, filter)) {
        cleanup();
        resolve(msg);
      }
    };
    socket.on('data', listener);

    // Emit-and-poll with the legacy backoff: emit, wait tm, double while
    // tm < 20000 (so the cadence is 1s, 2s, 4s, … capped after 32s).
    let tm = 1000;
    const tick = () => {
      if (cancelled || !listener) return;
      socket.emit('data', filter);
      timer = setTimeout(tick, tm);
      if (tm < 20000) tm *= 2;
    };
    tick();
  });

  return {
    promise,
    cancel: () => {
      cancelled = true;
      cleanup();
    },
  };
}

/**
 * POST `url` (form-encoded) → `{task_id}` → wait for the matching socket
 * `data` message (emit/backoff re-ask). Resolves with the FULL socket message
 * (the Sales tab reads `msg.data.sales_data` / `msg.data.generate_id`).
 */
export function ajaxPostToCelery(
  url: string,
  data: Record<string, FormValue>
): CeleryWait {
  let cancelled = false;
  let inner: CeleryWait | null = null;

  const promise = (async () => {
    const { task_id: taskId } = await postToCelery(url, data);
    if (cancelled) {
      // Cancelled before the POST resolved: no listener is ever attached and
      // the wait never settles (legacy parity — an abandoned wait just hangs).
      return new Promise<SocketDataMessage>(() => undefined);
    }
    inner = waitForCeleryTask(taskId);
    return inner.promise;
  })();

  // Swallow the POST failure for callers that don't attach a catch before
  // cancel — the caller's own .catch still observes it.
  promise.catch(() => undefined);

  return {
    promise,
    cancel: () => {
      cancelled = true;
      inner?.cancel();
    },
  };
}
